import { BottomSheet, type UseModalState } from "@clipboard-health/ui-components";
import { isDefined, isNil } from "@clipboard-health/util-ts";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useGetQualificationDisplayLabels } from "@src/appV2/Qualifications/api/useGetQualificationDisplayLabels";
import { parseISO } from "date-fns";
import { useMemo } from "react";

import { useDefinedWorker } from "../../../../Worker/useDefinedWorker";
import { SHIFT_DISCOVERY_SHIFT_BUNDLE_SELECT_MODAL_PATH } from "../../../ShiftDiscovery/paths";
import { useShiftModalsDataContext } from "../../../ShiftDiscovery/useShiftModalsDataContext";
import { isBookabilityAllowedWithBackgroundCheck } from "../../Bookability/isBookabilityAllowedWithBackgroundCheck";
import { resolveShiftType } from "../../resolveShiftType";
import { useBundleOffers } from "../hooks/useBundleOffers";
import { estimateBundleTotalPayInMinorUnits } from "../utils";
import { BundleFailedLoadingSheet } from "./BundleFailedLoadingSheet";
import { ShiftBundleBottomSheetFooter } from "./BundleFooter";
import { ShiftBundleBottomSheetContent } from "./Content";
import { ShiftBundleBottomSheetHeader } from "./Header";
import { ShiftBundleSheetSkeleton } from "./SheetSkeleton";

export interface ShiftBundleBottomSheetProps {
  modalState: UseModalState;
}

export function ShiftBundleBottomSheet(props: ShiftBundleBottomSheetProps) {
  const { modalState } = props;

  const {
    shift,
    bookabilityStatus,
    blockingRequirementsContext,
    hasHoursRestrictionConflict,
    shiftFailedLoading,
    isLoadingShiftModalsData,
    isIndependentContractorBadgesRequired,
    bundle,
    isBundleLoading,
    navigateToModal,
  } = useShiftModalsDataContext();

  const { workplace } = shift ?? {};

  const { bundleShiftPays, isLoading: isLoadingBundleOffers } = useBundleOffers({
    bundle,
    requiresLunchBreak: workplace?.attributes.requiresLunchBreak ?? false,
  });

  const worker = useDefinedWorker();

  const { data: qualificationDisplayLabels } = useGetQualificationDisplayLabels([
    bundle?.qualification ?? "",
  ]);
  const [qualificationText] = qualificationDisplayLabels;

  const isBookable =
    isBookabilityAllowedWithBackgroundCheck(bookabilityStatus) &&
    isNil(shift?.attributes.windowRestriction) &&
    !blockingRequirementsContext?.hasBlockingRequirements;

  const sortedBundleShifts = useMemo(
    () =>
      [...(bundle?.shifts ?? [])].sort(
        (a, b) => parseISO(a.attributes.start).getTime() - parseISO(b.attributes.start).getTime()
      ),
    [bundle]
  );

  const offer = shift?.offer;

  const anchorShiftPayInMinorUnits = isDefined(offer)
    ? Math.round(offer.attributes.totalPay * 100)
    : undefined;

  const estimatedBundleTotalPayInMinorUnits =
    isDefined(anchorShiftPayInMinorUnits) && isDefined(bundle)
      ? estimateBundleTotalPayInMinorUnits(anchorShiftPayInMinorUnits, bundle.totalShiftCount)
      : undefined;

  const hasRequiredData =
    isDefined(bundle) &&
    isDefined(shift) &&
    isDefined(workplace) &&
    isDefined(offer) &&
    bundleShiftPays.size === bundle?.totalShiftCount;

  const bundleTotalPayInMinorUnits = useMemo(
    () =>
      Array.from(bundleShiftPays.entries()).reduce(
        (accumulator, [, value]) => accumulator + value.totalPayInMinorUnits,
        0
      ),
    [bundleShiftPays]
  );

  const shiftType = hasRequiredData
    ? resolveShiftType({
        isUrgent: shift.attributes.isUrgent,
        window: shift.attributes.window,
        priorityTill: shift.attributes.priorityTill,
      })
    : undefined;

  const isLoading = isLoadingShiftModalsData || isLoadingBundleOffers || isBundleLoading;

  const isPayExact = bundleTotalPayInMinorUnits < 100_000;
  const totalPayInMinorUnits = isPayExact
    ? bundleTotalPayInMinorUnits
    : estimatedBundleTotalPayInMinorUnits;

  if (isLoading) {
    return (
      <BottomSheet modalState={modalState} paperSx={{ height: "100%" }}>
        <ShiftBundleSheetSkeleton
          onClose={() => {
            modalState.closeModal();
          }}
        />
      </BottomSheet>
    );
  }

  if (!hasRequiredData) {
    return <BundleFailedLoadingSheet modalState={modalState} bundleShiftPays={bundleShiftPays} />;
  }

  return (
    <BottomSheet
      modalState={modalState}
      paperSx={{ height: "100%" }}
      footer={
        <ShiftBundleBottomSheetFooter
          requiredShiftCount={bundle.requiredShiftCount}
          totalShiftCount={bundle.totalShiftCount}
          totalPayInMinorUnits={totalPayInMinorUnits}
          isPayExact={isPayExact}
          isInstantPay={shift.attributes.isInstantPay ?? false}
          isBookable={isBookable}
          onSelectShifts={() => {
            logEvent(APP_V2_USER_EVENTS.SHIFT_BUNDLE_SELECT_SHIFTS, {
              workplaceId: bundle.workplaceId,
              totalShiftCount: bundle.totalShiftCount,
              requiredShiftCount: bundle.requiredShiftCount,
              workerId: worker.userId,
              anchorShiftId: shift.id,
            });
            navigateToModal(SHIFT_DISCOVERY_SHIFT_BUNDLE_SELECT_MODAL_PATH, {
              pathParams: { shiftId: shift.id },
            });
          }}
        />
      }
    >
      <ShiftBundleBottomSheetHeader
        bundle={bundle}
        sortedBundleShifts={sortedBundleShifts}
        qualificationText={qualificationText ?? bundle.qualification}
        workplaceTimezone={workplace.attributes.location.timezone}
        onClose={() => {
          modalState.closeModal();
        }}
      />

      {isDefined(bookabilityStatus) && isDefined(shiftType) && (
        <ShiftBundleBottomSheetContent
          shiftId={shift.id}
          shiftType={shiftType}
          shiftQualification={shift.attributes.qualification}
          shiftStartIso={shift.attributes.start}
          workplace={workplace}
          bookabilityStatus={bookabilityStatus}
          blockingRequirementsContext={blockingRequirementsContext}
          hasHoursRestrictionConflict={hasHoursRestrictionConflict ?? false}
          shiftFailedLoading={shiftFailedLoading}
          hasMarkedInterest={shift.attributes.isInterested}
          restrictedToWindow={shift.attributes.windowRestriction}
          isIndependentContractorBadgesRequired={isIndependentContractorBadgesRequired}
          window={shift.attributes.window}
          sortedBundleShifts={sortedBundleShifts}
          bundleShiftPays={bundleShiftPays}
        />
      )}
    </BottomSheet>
  );
}
